import React, { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useBranding } from "@/lib/branding";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Upload, Trash2, CheckCircle2, ShieldAlert, Users, Building2 } from "lucide-react";

/**
 * `/affiliate/apply` — Signed 2026-season software affiliate contract.
 * Public form; submitted contracts go into `db.affiliate_contracts` for the
 * ERO to review from `/admin/affiliate-contracts`.
 */

const TIERS = [
  { key: "efin_100_plus",    label: "EFIN — 100+ funded returns last season",      split: "90 / 10" },
  { key: "efin_under_100",   label: "EFIN — under 100 funded returns",             split: "80 / 20" },
  { key: "ptin_2yr_50_plus", label: "PTIN 2+ years — 50+ funded returns",          split: "70 / 30" },
  { key: "ptin_2yr_under_50",label: "PTIN 2+ years — under 50 funded returns",     split: "60 / 40" },
  { key: "ptin_first_year",  label: "PTIN first year",                             split: "60 / 40" },
];

const UPLOAD_KINDS = [
  { key: "id",   label: "Government-issued ID (front)", required: true, condition: () => true },
  { key: "ptin", label: "PTIN card / IRS PTIN letter",   required: true, condition: () => true },
  { key: "funded_returns", label: "IRS funded-returns page — last season", required: true, condition: (tier) => tier && tier !== "ptin_first_year",
    hint: "Screenshot or PDF of your funded-returns page from your IRS e-Services account so we can verify your prior-year volume." },
  { key: "efin", label: "EFIN letter (only if EFIN tier)", required: false, condition: () => true },
];

const EMPTY = {
  full_name: "", email: "", phone: "", secondary_phone: "",
  address: "", city: "", state: "", zip: "",
  ptin: "", efin: "",
  tier: "", estimated_returns: "",
  payment_method: "", payment_handle: "",
  uploads: {},
  initials_splits: "", initials_payment: "", initials_paperwork: "", initials_fraud: "",
  signature: "", signed_date: new Date().toISOString().slice(0, 10),
  agree_read: false, agree_no_refund: false,
};

export default function AffiliateApply() {
  const { branding } = useBranding();
  const nav = useNavigate();
  const [f, setF] = useState(EMPTY);
  const [uploadingKind, setUploadingKind] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }));

  const uploadFiles = async (kind, filelist) => {
    if (!filelist || !filelist.length) return;
    setUploadingKind(kind);
    const added = [];
    for (const file of Array.from(filelist)) {
      if (file.size > 20 * 1024 * 1024) { toast.error(`${file.name}: too large (20 MB max)`); continue; }
      const form = new FormData();
      form.append("file", file);
      try {
        const r = await api.post(`/affiliate/upload?kind=${kind}`, form, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(r.data.path);
      } catch (e) {
        toast.error(`${file.name}: ${e?.response?.data?.detail || "upload failed"}`);
      }
    }
    if (added.length) {
      setF(prev => ({
        ...prev,
        uploads: { ...prev.uploads, [kind]: [...(prev.uploads[kind] || []), ...added] },
      }));
      toast.success(`${added.length} file${added.length === 1 ? "" : "s"} uploaded`);
    }
    setUploadingKind(null);
  };

  const removeUpload = (kind, idx) => {
    setF(prev => ({
      ...prev,
      uploads: { ...prev.uploads, [kind]: (prev.uploads[kind] || []).filter((_, i) => i !== idx) },
    }));
  };

  const isEfinTier = useMemo(() => f.tier.startsWith("efin"), [f.tier]);

  /* DocuSign-style progress — count how many required steps are complete. */
  const progress = useMemo(() => {
    const requiresFunded = f.tier && f.tier !== "ptin_first_year";
    const steps = [
      { key: "name", label: "Your name", done: !!f.full_name.trim() },
      { key: "email", label: "Email", done: !!f.email.trim() },
      { key: "ptin", label: "PTIN #", done: !!f.ptin.trim() },
      { key: "tier", label: "Tier", done: !!f.tier },
      { key: "upload_id", label: "ID upload", done: !!(f.uploads.id || []).length },
      { key: "upload_ptin", label: "PTIN upload", done: !!(f.uploads.ptin || []).length },
      ...(requiresFunded ? [{ key: "upload_funded", label: "Funded returns", done: !!(f.uploads.funded_returns || []).length }] : []),
      { key: "init_1", label: "Initials · splits", done: !!f.initials_splits.trim() },
      { key: "init_2", label: "Initials · payment", done: !!f.initials_payment.trim() },
      { key: "init_3", label: "Initials · paperwork", done: !!f.initials_paperwork.trim() },
      { key: "init_4", label: "Initials · fraud", done: !!f.initials_fraud.trim() },
      { key: "agree_read", label: "Read & understood", done: f.agree_read },
      { key: "agree_no_refund", label: "No-refund policy", done: f.agree_no_refund },
      { key: "sign", label: "Signature", done: !!f.signature.trim() },
    ];
    const done = steps.filter(s => s.done).length;
    return { steps, done, total: steps.length, pct: Math.round((done / steps.length) * 100) };
  }, [f]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.agree_read) { toast.error("You must confirm you have read the contract."); return; }
    if (!f.agree_no_refund) { toast.error("You must acknowledge the no-refund policy."); return; }
    if (!f.tier) { toast.error("Please pick a tier."); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/affiliate", f);
      setDone({ id: r.data.id });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-background paper-texture p-6 py-12">
        <div className="max-w-2xl mx-auto bg-card border border-border rounded-md p-10 text-center" data-testid="affiliate-success">
          <CheckCircle2 size={56} className="mx-auto text-primary mb-4" />
          <div className="label-caps mb-2">Received</div>
          <h1 className="font-serif text-4xl mb-3">Welcome to the team.</h1>
          <p className="text-muted-foreground mb-6">
            Your signed contract is in. Di'Meka will review your paperwork and reach out within 1-2 business days with next steps — including your TaxSlayer Pro credentials.
          </p>
          <div className="rounded-md border border-primary/40 bg-primary/5 p-4 mb-6 text-left">
            <div className="label-caps text-[10px] mb-2 text-primary">Save this reference</div>
            <div className="text-sm">Contract ID: <span className="font-mono">{done.id.slice(0, 8)}</span></div>
            <div className="text-xs text-muted-foreground pt-1">Email on file: {f.email}</div>
          </div>
          <Button onClick={() => nav("/tax-pros")} className="w-full" data-testid="affiliate-done-btn">Back to Tax Pros</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background paper-texture">
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto p-6 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            {branding.logo_url ? <img src={branding.logo_url} alt="" className="h-9 w-9 object-contain" /> :
              <div className="h-9 w-9 rounded-md bg-primary text-primary-foreground grid place-items-center font-serif">{branding.brand_name?.[0]}</div>}
            <div className="font-serif text-2xl">{branding.brand_name}</div>
          </Link>
          <div className="label-caps hidden sm:block">Affiliate Contract · 2026</div>
        </div>
      </header>

      {/* Sticky progress bar — like DocuSign. */}
      <div className="sticky top-0 z-30 bg-background/95 backdrop-blur border-b border-border" data-testid="aff-progress">
        <div className="max-w-3xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between mb-1.5">
            <div className="text-xs font-medium">
              {progress.done === progress.total ? (
                <span className="text-primary inline-flex items-center gap-1"><CheckCircle2 size={13} /> All set — ready to submit</span>
              ) : (
                <span className="text-foreground">{progress.done} of {progress.total} complete</span>
              )}
            </div>
            <div className="text-xs text-muted-foreground">
              {progress.total - progress.done > 0
                ? `${progress.total - progress.done} step${progress.total - progress.done === 1 ? "" : "s"} left`
                : "100%"}
            </div>
          </div>
          <div className="h-2 rounded-full bg-secondary overflow-hidden">
            <div
              className="h-full bg-primary transition-all duration-300"
              style={{ width: `${progress.pct}%` }}
              data-testid="aff-progress-bar"
            />
          </div>
          {progress.done < progress.total && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {progress.steps.filter(s => !s.done).slice(0, 6).map(s => (
                <span key={s.key} className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground border border-border" data-testid={`aff-missing-${s.key}`}>
                  {s.label}
                </span>
              ))}
              {progress.steps.filter(s => !s.done).length > 6 && (
                <span className="text-[10px] text-muted-foreground">+{progress.steps.filter(s => !s.done).length - 6} more</span>
              )}
            </div>
          )}
        </div>
      </div>

      <form onSubmit={submit} className="max-w-3xl mx-auto p-6 lg:p-12 space-y-8" data-testid="affiliate-form">
        <div>
          <div className="label-caps mb-3 text-primary">DyNamic Tax software affiliate onboarding</div>
          <h1 className="font-serif text-4xl lg:text-6xl leading-none mb-3">Join the team.</h1>
          <p className="text-muted-foreground max-w-xl">
            Fill this contract out completely, upload your ID + PTIN, sign, and hit submit. Di'Meka (ERO) reviews within 1-2 business days and sends your TaxSlayer Pro credentials once approved.
          </p>
        </div>

        {/* Personal */}
        <Section title="Your information">
          <Row>
            <Field label="Full legal name" required><Input required data-testid="aff-name" value={f.full_name} onChange={e => set("full_name", e.target.value)} /></Field>
            <Field label="Email" required><Input required type="email" data-testid="aff-email" value={f.email} onChange={e => set("email", e.target.value)} /></Field>
          </Row>
          <Row>
            <Field label="Primary phone"><Input type="tel" value={f.phone} onChange={e => set("phone", e.target.value)} /></Field>
            <Field label="Secondary phone (backup)"><Input type="tel" value={f.secondary_phone} onChange={e => set("secondary_phone", e.target.value)} /></Field>
          </Row>
          <Field label="Street address"><Input value={f.address} onChange={e => set("address", e.target.value)} /></Field>
          <Row>
            <Field label="City"><Input value={f.city} onChange={e => set("city", e.target.value)} /></Field>
            <Field label="State"><Input value={f.state} onChange={e => set("state", e.target.value)} /></Field>
            <Field label="ZIP"><Input value={f.zip} onChange={e => set("zip", e.target.value)} /></Field>
          </Row>
          <Row>
            <Field label="PTIN #" required><Input required placeholder="P00000000" value={f.ptin} onChange={e => set("ptin", e.target.value)} data-testid="aff-ptin" /></Field>
            {isEfinTier && <Field label="EFIN #" required><Input required placeholder="000000" value={f.efin} onChange={e => set("efin", e.target.value)} data-testid="aff-efin" /></Field>}
          </Row>
        </Section>

        {/* Tier */}
        <Section title="Pick your tier" icon={<Users size={16} />}>
          <p className="text-sm text-muted-foreground -mt-2 mb-2">
            The <strong className="text-foreground">first number</strong> is the preparer's split, the second is the ERO's. Preparer always receives the larger share.
          </p>
          <div className="space-y-2" data-testid="aff-tier-list">
            {TIERS.map(t => (
              <label key={t.key} className={`flex items-start gap-3 p-4 rounded-md border cursor-pointer transition-colors ${f.tier === t.key ? "border-primary bg-primary/5" : "border-border hover:border-primary"}`} data-testid={`aff-tier-${t.key}`}>
                <input type="radio" name="tier" value={t.key} checked={f.tier === t.key} onChange={() => set("tier", t.key)} className="mt-1" />
                <div className="flex-1">
                  <div className="text-sm font-medium">{t.label}</div>
                  <div className="text-xs text-muted-foreground">Split · <span className="font-mono text-foreground">{t.split}</span></div>
                </div>
              </label>
            ))}
          </div>
          <Row>
            <Field label="Estimated returns this season"><Input inputMode="numeric" placeholder="e.g., 75" value={f.estimated_returns} onChange={e => set("estimated_returns", e.target.value)} /></Field>
          </Row>
        </Section>

        {/* Payment info */}
        <Section title="How would you like to be paid?" icon={<Building2 size={16} />}>
          <p className="text-sm text-muted-foreground -mt-2 mb-2">
            You'll be paid your portion of each funded return. Pick a delivery method.
          </p>
          <Row>
            <Field label="Payment method">
              <select value={f.payment_method} onChange={e => set("payment_method", e.target.value)} className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm" data-testid="aff-payment-method">
                <option value="">Choose…</option>
                <option value="cashapp">Cash App</option>
                <option value="ach">Direct deposit / ACH</option>
                <option value="check">Check</option>
              </select>
            </Field>
            <Field label={f.payment_method === "ach" ? "Bank name / account details" : "Handle / email / phone"}>
              <Input value={f.payment_handle} onChange={e => set("payment_handle", e.target.value)} placeholder={f.payment_method === "cashapp" ? "$YourTag" : ""} data-testid="aff-payment-handle" />
            </Field>
          </Row>
        </Section>

        {/* Uploads */}
        <Section title="Upload your paperwork">
          <p className="text-sm text-muted-foreground -mt-2 mb-3">ID and PTIN are required. If you filed returns last season, upload your <strong>IRS funded-returns page</strong> so we can verify your prior volume. EFIN letter required if you selected an EFIN tier.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            {UPLOAD_KINDS.filter(k => k.condition(f.tier)).map(k => {
              const requiredNow = k.required && (k.key !== "funded_returns" || f.tier !== "ptin_first_year");
              return (
                <Card key={k.key} className="p-4">
                  <div className="text-sm font-medium mb-1">
                    {k.label}
                    {requiredNow && <span className="text-destructive ml-0.5">*</span>}
                  </div>
                  {k.hint && <div className="text-[10px] text-muted-foreground mb-2 leading-snug">{k.hint}</div>}
                  <div className="text-[10px] text-muted-foreground mb-2">
                    {(f.uploads[k.key] || []).length} file{(f.uploads[k.key] || []).length === 1 ? "" : "s"} attached
                  </div>
                  <label className="inline-flex w-full">
                    <input type="file" multiple accept="application/pdf,image/*" className="hidden" data-testid={`aff-upload-${k.key}`} onChange={e => uploadFiles(k.key, e.target.files)} />
                    <Button asChild size="sm" variant="outline" className="w-full" disabled={uploadingKind === k.key}>
                      <span><Upload size={12} className="mr-1" /> {uploadingKind === k.key ? "Uploading…" : "Choose files"}</span>
                    </Button>
                  </label>
                  {(f.uploads[k.key] || []).map((p, idx) => (
                    <div key={p} className="text-[10px] font-mono mt-1 flex items-center justify-between gap-2">
                      <span className="truncate min-w-0">…{p.slice(-24)}</span>
                      <button type="button" onClick={() => removeUpload(k.key, idx)} className="text-destructive" aria-label="Remove"><Trash2 size={10} /></button>
                    </div>
                  ))}
                </Card>
              );
            })}
          </div>
        </Section>

        {/* CONTRACT TEXT */}
        <Section title="Contract" icon={<ShieldAlert size={16} />}>
          <div className="rounded-md border border-border bg-secondary/30 p-5 text-sm space-y-3 leading-relaxed" data-testid="aff-contract-body">
            <p>
              This contract is entered into between <strong>{branding.brand_name || "DyNamic Tax"}</strong> ("ERO") and <strong>{f.full_name || "the Affiliate"}</strong> ("Affiliate"), effective the date the software is purchased, for the <strong>2026 tax season</strong>.
            </p>
            <p>
              <strong>Software included:</strong> TaxSlayer Pro and Bank Products (cash advances included). Mentorship is included with every package. Software is on sale for <strong>$150 until October 31st</strong>; the price is <strong>$300</strong> thereafter.
            </p>
            <p>
              <strong>Payment splits:</strong> The Affiliate always receives the larger share of the split. The ERO ({branding.brand_name || "DyNamic Tax"}) receives the smaller share.
            </p>
            <p>
              <strong>Fraud disclaimer:</strong> If the Affiliate is found to be engaged in any fraudulent activity, they will be terminated from the software immediately. {branding.brand_name || "DyNamic Tax"} is not responsible for any bank fees that come in after dismissal. After the first return is submitted, there are <strong>no refunds</strong> for the software.
            </p>
          </div>

          <div className="space-y-3 mt-4">
            <InitialLine
              testid="init-splits"
              text={<>Affiliate is <strong>knowledgeable of the splits</strong> and how they work.</>}
              value={f.initials_splits}
              onChange={v => set("initials_splits", v)}
            />
            <InitialLine
              testid="init-payment"
              text={<>Affiliate understands they <strong>will get paid when returns are funded</strong> — not before.</>}
              value={f.initials_payment}
              onChange={v => set("initials_payment", v)}
            />
            <InitialLine
              testid="init-paperwork"
              text={<>Affiliate acknowledges <strong>all paperwork for every week must be sent in to the ERO</strong>. If documents are not received the Affiliate will be locked out of the software until appropriate documents are sent.</>}
              value={f.initials_paperwork}
              onChange={v => set("initials_paperwork", v)}
            />
            <InitialLine
              testid="init-fraud"
              text={<>Affiliate understands they can be <strong>locked out of the system if any fraudulent activity occurs</strong>. Once locked out, Affiliate will <strong>not</strong> be compensated for any fees that come in after removal of the software.</>}
              value={f.initials_fraud}
              onChange={v => set("initials_fraud", v)}
            />
          </div>

          <div className="rounded-md bg-secondary/40 border border-border p-4 text-sm mt-4">
            <label className="flex items-start gap-3">
              <input type="checkbox" checked={f.agree_read} onChange={e => set("agree_read", e.target.checked)} data-testid="aff-agree-read" className="mt-1" />
              <span>I have <strong>read and understood</strong> this contract in full.</span>
            </label>
          </div>
          <div className="rounded-md bg-secondary/40 border border-border p-4 text-sm">
            <label className="flex items-start gap-3">
              <input type="checkbox" checked={f.agree_no_refund} onChange={e => set("agree_no_refund", e.target.checked)} data-testid="aff-agree-no-refund" className="mt-1" />
              <span>I acknowledge <strong>no refunds are issued once the first return is submitted</strong> through the software.</span>
            </label>
          </div>

          <Row>
            <Field label="Type your full name to sign" required>
              <Input required data-testid="aff-signature" value={f.signature} onChange={e => set("signature", e.target.value)} placeholder="Your legal name" />
            </Field>
            <Field label="Date" required>
              <Input required type="date" value={f.signed_date} onChange={e => set("signed_date", e.target.value)} data-testid="aff-signed-date" />
            </Field>
          </Row>
        </Section>

        <div className="flex flex-wrap justify-between gap-3 pt-4 border-t border-border">
          <Button type="button" variant="ghost" onClick={() => nav("/tax-pros")}>Back</Button>
          <Button type="submit" size="lg" disabled={submitting} data-testid="aff-submit-btn">
            {submitting ? "Submitting…" : "Sign & submit contract"}
          </Button>
        </div>
      </form>
    </div>
  );
}

const Section = ({ title, icon, children }) => (
  <section className="space-y-4">
    <div className="border-b border-border pb-2 flex items-center gap-2">
      {icon && <span className="text-primary">{icon}</span>}
      <h2 className="font-serif text-2xl">{title}</h2>
    </div>
    {children}
  </section>
);

const Row = ({ children }) => <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">{children}</div>;

const Field = ({ label, required, children }) => (
  <div className="space-y-1">
    <Label className="label-caps text-[10px]">{label}{required && <span className="text-destructive ml-0.5">*</span>}</Label>
    {children}
  </div>
);

function InitialLine({ testid, text, value, onChange }) {
  return (
    <div className="flex items-start gap-3 p-3 rounded-md border border-border bg-card" data-testid={testid}>
      <div className="flex-1 text-sm leading-relaxed">{text}</div>
      <div className="shrink-0">
        <Label className="label-caps text-[9px] block mb-1">Initials</Label>
        <Input
          maxLength={4}
          value={value}
          onChange={e => onChange(e.target.value.toUpperCase())}
          className="w-20 text-center font-mono tracking-widest"
          data-testid={`${testid}-input`}
          placeholder="AB"
        />
      </div>
    </div>
  );
}
